import { EvidenceBlob } from './martech-scanner-constants';
import { TagFire } from './tag-fire-constants';
import {
  DataLayerVariableInventory,
  DataLayerVariableSource,
  DataLayerVariableSourceType
} from './datalayer-inventory-constants';

const MAX_FLATTEN_DEPTH = 6;
const MAX_FLATTENED_KEYS = 2000; // guard against a pathological live-snapshot shape

// GTM's own bootstrap/listener-generated pushes (container load, click/form/
// history listeners, ...) - same "event"-prefix convention the
// dataLayer.push() code extractor already uses (tag-fire-utils.ts) to
// exclude these from being decoded as real tag fires. A live snapshot sees
// window.dataLayer's *entire* history, GTM housekeeping included, so
// without this every "available variable" list on a GTM-managed site would
// be dominated by internal keys (gtm.start, gtm.uniqueEventId, gtm.triggers,
// ...) that no one would ever call a tagging variable.
const isGtmInternalEntry = (entry: unknown): boolean => {
  if (!entry || typeof entry !== 'object') {
    return false;
  }
  const { event } = entry as Record<string, unknown>;
  return typeof event === 'string' && event.startsWith('gtm.');
};

/**
 * Recursively flattens an object/array into dotted-path keys (e.g.
 * "ecommerce.items"), depth- and count-capped so a pathological live
 * snapshot can't blow up the inventory. Keys named "gtm.*" are skipped too
 * (belt-and-suspenders alongside isGtmInternalEntry, for the rare case one
 * shows up on an otherwise-custom entry).
 */
const flattenKeysInto = (value: unknown, prefix: string, depth: number, out: Set<string>): void => {
  if (out.size >= MAX_FLATTENED_KEYS || depth > MAX_FLATTEN_DEPTH) {
    return;
  }
  if (value === null || typeof value !== 'object') {
    return;
  }
  if (Array.isArray(value)) {
    value.forEach(item => flattenKeysInto(item, prefix, depth + 1, out));
    return;
  }
  Object.entries(value as Record<string, unknown>).forEach(([key, val]) => {
    if (out.size >= MAX_FLATTENED_KEYS || key.startsWith('gtm.')) {
      return;
    }
    const path = prefix ? `${prefix}.${key}` : key;
    out.add(path);
    flattenKeysInto(val, path, depth + 1, out);
  });
};

/**
 * Finds the index of the bracket that closes the one at `openIndex`,
 * skipping over brackets inside string literals. Returns -1 if unbalanced
 * (a truncated/malformed container payload).
 */
const findMatchingBracket = (text: string, openIndex: number): number => {
  const open = text[openIndex];
  const close = open === '[' ? ']' : '}';
  let depth = 0;
  let inString = false;
  let stringChar = '';
  for (let i = openIndex; i < text.length; i += 1) {
    const ch = text[i];
    if (inString) {
      if (ch === '\\') {
        i += 1; // skip the escaped character - it can't close the string
      } else if (ch === stringChar) {
        inString = false;
      }
      continue;
    }
    if (ch === '"' || ch === "'") {
      inString = true;
      stringChar = ch;
    } else if (ch === open) {
      depth += 1;
    } else if (ch === close) {
      depth -= 1;
      if (depth === 0) {
        return i;
      }
    }
  }
  return -1;
};

/**
 * Structurally extracts GTM Data Layer Variable ("__v") names from a
 * fetched container's own payload - the container already has this data
 * (see bpm-lambdas' scanService.ts, which fetches the full gtm.js text into
 * a `gtm-container:` evidence blob), it's just never been parsed before.
 * Locates the container's "macros" array and JSON.parses just that slice
 * rather than regex-scraping it, since a Data Layer Variable definition is
 * a real nested object ({"function":"__v","vtp_name":"ecommerce.value"}),
 * not a flat key/value pair.
 */
export const extractContainerVariableNames = (blobs: EvidenceBlob[]): string[] => {
  const names = new Set<string>();
  blobs
    .filter(blob => blob.source.startsWith('gtm-container:'))
    .forEach(blob => {
      const marker = '"macros":[';
      const markerIndex = blob.text.indexOf(marker);
      if (markerIndex === -1) {
        return;
      }
      const arrayStart = markerIndex + marker.length - 1;
      const arrayEnd = findMatchingBracket(blob.text, arrayStart);
      if (arrayEnd === -1) {
        return;
      }
      try {
        const macros = JSON.parse(blob.text.slice(arrayStart, arrayEnd + 1));
        if (!Array.isArray(macros)) {
          return;
        }
        macros.forEach(macro => {
          if (
            macro &&
            typeof macro === 'object' &&
            macro.function === '__v' &&
            typeof macro.vtp_name === 'string'
          ) {
            names.add(macro.vtp_name);
          }
        });
      } catch {
        // Malformed/truncated container payload - skip this blob, non-fatal.
      }
    });
  return [...names].sort();
};

/**
 * Unions every discovered tagging-relevant variable (GTM dataLayer fires'
 * decoded params, GTM container variable definitions, a live runtime
 * snapshot when available) against every raw param key already consumed by
 * a decoded tag fire, on any platform - so a variable that exists but that
 * no known tag reads becomes visible.
 */
export const buildDataLayerInventory = (
  evidenceBlobs: EvidenceBlob[],
  fires: TagFire[],
  liveSnapshots?: { preConsent?: unknown[]; postConsent?: unknown[] }
): DataLayerVariableInventory => {
  const sources: Record<string, Set<DataLayerVariableSourceType>> = {};
  const addSource = (key: string, source: DataLayerVariableSourceType): void => {
    if (!key) {
      return;
    }
    sources[key] = sources[key] ?? new Set();
    sources[key].add(source);
  };

  fires
    .filter(f => f.platform === 'GTM dataLayer')
    .forEach(f =>
      Object.keys(f.params).forEach(key => addSource(key, DataLayerVariableSource.DATALAYER_PUSH))
    );

  extractContainerVariableNames(evidenceBlobs).forEach(name =>
    addSource(name, DataLayerVariableSource.GTM_CONTAINER)
  );

  const liveKeys = new Set<string>();
  [...(liveSnapshots?.preConsent ?? []), ...(liveSnapshots?.postConsent ?? [])]
    .filter(entry => !isGtmInternalEntry(entry))
    .forEach(entry => flattenKeysInto(entry, '', 0, liveKeys));
  liveKeys.forEach(key => addSource(key, DataLayerVariableSource.LIVE_SNAPSHOT));

  const consumed = new Set<string>();
  fires.forEach(f => Object.keys(f.params).forEach(key => consumed.add(key)));

  const available = Object.keys(sources).sort();

  return {
    available,
    consumed: [...consumed].sort(),
    unused: available.filter(key => !consumed.has(key)),
    sources: Object.fromEntries(Object.entries(sources).map(([key, set]) => [key, [...set].sort()]))
  };
};

/**
 * Unions two already-built inventories (e.g. a quick scan's and a full
 * scan's - see full-scan-merge-utils.ts) rather than recomputing from
 * evidence, since the quick scan's raw evidenceBlobs aren't retained past
 * scan time. Per-key sources merge (a variable known from either scan is
 * known), and unused/available are recomputed from the merged sets so a
 * variable consumed in only one scan is still correctly marked used.
 */
export const mergeDataLayerInventories = (
  a: DataLayerVariableInventory,
  b: DataLayerVariableInventory
): DataLayerVariableInventory => {
  const sources: Record<string, Set<DataLayerVariableSourceType>> = {};
  [a, b].forEach(inv => {
    Object.entries(inv.sources).forEach(([key, srcs]) => {
      sources[key] = sources[key] ?? new Set();
      srcs.forEach(s => sources[key].add(s));
    });
  });
  const consumed = Array.from(new Set([...a.consumed, ...b.consumed])).sort();
  const available = Object.keys(sources).sort();
  return {
    available,
    consumed,
    unused: available.filter(key => !consumed.includes(key)),
    sources: Object.fromEntries(Object.entries(sources).map(([key, set]) => [key, [...set].sort()]))
  };
};
